import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Flex } from '@/UI/styles';
import NodeDataCard from '@/components/Common/NodeDataCard/NodeDataCard';
import { I18N_KEYS } from '@/language/keys';
import { useWorkflowStore } from '@/state/workflow.state';
import { useUserStore } from '@/state/user.state';
import { useMemberName } from '@/queries/useWorkspace';
import { getModelPriceInfo } from '@/components/Nodes/Utils';
import type { ModelItem } from '@/state/nodes/nodes.types';
import type { SubflowTemplateResponseDto } from '@/types/api/subflowTemplate';
import type { AiModelData } from '@/types/nodes/model';

export const NodeDetails = ({
  item,
  shouldDisplayEmptyOutput,
  template,
}: {
  item: ModelItem;
  shouldDisplayEmptyOutput?: boolean;
  template?: SubflowTemplateResponseDto;
}) => {
  const { t } = useTranslation();
  const nodeTypes = useWorkflowStore((state) => state.nodeTypes);
  const modelPricesMaps = useWorkflowStore((state) => state.modelPricesMaps);
  const workspaceFalByokEnabled = useWorkflowStore((state) => state.workspaceFalByokEnabled);
  const userId = useUserStore((state) => state.user?.id);
  const memberName = useMemberName(template?.createdBy);

  // TODO(backend): once the menu endpoint populates `price` on saved items, this lookup
  // becomes unnecessary — item.price will already have the correct value and the early
  // return on `item.price != null` will always fire. See 03-design.md "Backend changes needed".
  const templatePrice = useMemo(() => {
    if (item.price != null || !template?.data?.nodes?.[0]) {
      return undefined;
    }
    const nodeData = template.data.nodes[0].data as AiModelData;
    if (!nodeData?.model) {
      return undefined;
    }
    return getModelPriceInfo(nodeData.model, modelPricesMaps, nodeData.params, { workspaceFalByokEnabled });
  }, [item.price, template?.data?.nodes, modelPricesMaps, workspaceFalByokEnabled]);

  const sourceNodeName = useMemo(() => {
    if (!template?.sourceNodeDefinitionId) {
      return undefined;
    }
    const nodeDef = nodeTypes.find((nd) => nd.id === template.sourceNodeDefinitionId);
    if (!nodeDef) {
      return undefined;
    }
    return t(I18N_KEYS.RECIPE_MAIN.FLOW.LEFT_PANEL.NODE_DETAILS.BASED_ON, { name: nodeDef.data?.name });
  }, [template?.sourceNodeDefinitionId, nodeTypes, t]);

  const createdByName = useMemo(() => {
    if (!template?.createdBy) {
      return undefined;
    }
    if (template.createdBy === userId) {
      return t(I18N_KEYS.RECIPE_MAIN.FLOW.LEFT_PANEL.NODE_DETAILS.CREATED_BY_ME);
    }
    if (!memberName) {
      return undefined;
    }
    return t(I18N_KEYS.RECIPE_MAIN.FLOW.LEFT_PANEL.NODE_DETAILS.CREATED_BY, { name: memberName });
  }, [template?.createdBy, userId, memberName, t]);

  return (
    <Flex sx={{ width: 320 }}>
      <NodeDataCard
        displayName={item.displayName}
        description={item.description ?? ''}
        price={item.price ?? templatePrice?.credits}
        isByok={item.isByok === true || templatePrice?.isByok === true}
        isVerified={item.isVerified}
        termsUrls={item.termsUrls ?? []}
        privacyUrl={item.privacyPolicyUrl ?? ''}
        inputTypes={item.inputTypes}
        outputTypes={item.outputTypes}
        shouldDisplayEmptyOutput={shouldDisplayEmptyOutput}
        sourceNodeName={sourceNodeName}
        createdByName={createdByName}
      />
    </Flex>
  );
};
